/**
 * Infinite Scroll Collection
 * Custom element pour le chargement infini des produits dans les collections
 *
 * Trois phases enchaînées (cf. main-collection.liquid) :
 *   1. 'main'   — produits en stock de la collection courante (#collection-grid)
 *   2. 'parent' — produits en stock de la collection mère (#collection-grid-parent),
 *                 chargés depuis /collections/<mère> avec les filtres et le tri courants,
 *                 dédoublonnés de tout ce qui est déjà affiché
 *   3. 'done'   — dévoilement des produits hors stock de la collection courante
 *                 (#collection-grid-oos), accumulés au fil des lots
 *
 * Le serveur ne peut pas produire cet ordre seul : Liquid pagine la collection telle quelle
 * et ne sait pas appliquer les filtres de la collection courante à une autre collection.
 */
if (!customElements.get('infinite-scroll-collection')) {
  const OBSERVER_ROOT_MARGIN = '400px';
  const STAGGER_DELAY_MS = 50;
  const ANIMATION_CLEANUP_MS = 500;
  const RESTORE_TIMEOUT_MS = 8000;
  const RESTORE_CHUNK_SIZE = 3;
  const MAX_RETRY_COUNT = 3;
  const OFFLOAD_ROOT_MARGIN = '4000px';
  // Lots successifs sans rien à afficher tolérés avant d'abandonner (page 100 % hors stock,
  // page entièrement dédoublonnée…). Borne les requêtes en chaîne sur un seul loadMore().
  const MAX_EMPTY_FETCHES = 5;
  // Au-delà, on considère que la collection n'a (presque) rien en stock et on repasse en
  // affichage dégradé : cf. _degradeSplit().
  const MAX_EMPTY_STREAK = 6;
  /** Catégories suggérées essayées avant d'abandonner le bloc « pourraient vous intéresser ». */
  const PARENT_CANDIDATES_MAX = 3;
  /** Grille d'un lot de collection, côté réponse serveur. */
  const GRID_SELECTOR = '#collection-grid';

  class InfiniteScrollCollection extends HTMLElement {
    constructor() {
      super();
      this.nextUrl = this.dataset.nextUrl || null;
      this.parentUrl = this.dataset.parentUrl || null;
      // `/search` n'a pas de metafield `data.parent_collection` : la collection mère y est
      // résolue depuis les catégories suggérées par la recherche prédictive pour ce terme, puis
      // élargie à LEUR propre collection mère (décision utilisateur 2026-09-16).
      // Facultatif — cf. _resolveParentUrlFromSearch.
      this.parentSearchTerm = this.dataset.parentFromSearch || null;
      this._parentUrlAttempted = false;
      this.phase = 'main';
      this.isLoading = false;
      this.hasMore = !!this.nextUrl;
      this._batchCount = 0;
      this._retryCount = 0;
      this._abortController = null;
      this._animationTimers = [];
      this._postInsertDebounceId = null;
      this._seenHandles = new Set();
      this._emptyStreak = 0;
      this._oosRevealed = false;
      this._splitDegraded = false;
    }

    connectedCallback() {
      this.grid = this.querySelector('#collection-grid');
      this.sentinel = this.querySelector('[data-sentinel]');
      this.loader = this.querySelector('[data-loader]');
      this.endMessage = this.querySelector('[data-end-message]');
      this.nextLink = this.querySelector('[data-infinite-next]');

      this.parentZone = this.querySelector('[data-split-parent]');
      this.parentGrid = this.parentZone ? this.parentZone.querySelector('#collection-grid-parent') : null;
      this.parentSeparator = this.parentZone ? this.parentZone.querySelector('[data-split-separator]') : null;
      this.oosZone = this.querySelector('[data-split-oos]');
      this.oosGrid = this.oosZone ? this.oosZone.querySelector('#collection-grid-oos') : null;
      this.oosSeparator = this.oosZone ? this.oosZone.querySelector('[data-split-separator]') : null;
      this.emptyMessage = this.querySelector('[data-split-empty]');

      if (!this.grid || !this.sentinel) {
        console.warn('InfiniteScrollCollection: Missing required elements');
        return;
      }

      this._registerHandles(this.querySelectorAll('.grid__item[data-product-handle]'));

      // Les hors stock ne se dévoilent qu'une fois tout le reste chargé. Sans JS le bloc reste
      // visible : l'ordre rendu par le serveur (en stock puis hors stock) est déjà le bon.
      // _initNormal() lève le masquage tout de suite si la collection tient en un seul lot.
      this._syncOutOfStockVisibility();

      this._abortController = new AbortController();

      if (this._shouldRestore()) {
        this._restoreState();
        return;
      }

      this._initNormal();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this._offloadObserver?.disconnect();
      this._abortController?.abort();
      this._animationTimers.forEach(clearTimeout);
      this._animationTimers = [];
      clearTimeout(this._postInsertDebounceId);
      if (this._pagehideHandler) {
        window.removeEventListener('pagehide', this._pagehideHandler);
      }
      if (this._pageshowHandler) {
        window.removeEventListener('pageshow', this._pageshowHandler);
      }
    }

    initObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.isLoading && this.hasMore) {
            this.loadMore();
          }
        },
        {
          rootMargin: OBSERVER_ROOT_MARGIN,
          threshold: 0,
        },
      );
      this.observer.observe(this.sentinel);
    }

    async loadMore() {
      if (this.isLoading || !this.hasMore) return;
      this.isLoading = true;
      this.showLoader();

      try {
        let inserted = 0;
        let emptyFetches = 0;

        // Un lot peut ne rien apporter à la grille visible (lot 100 % hors stock en phase
        // principale, lot entièrement dédoublonné en phase collection mère) : on enchaîne
        // sur le suivant au lieu de conclure à tort que tout est chargé.
        while (this.hasMore && inserted === 0 && emptyFetches < MAX_EMPTY_FETCHES) {
          const phase = this.phase;
          const { products, oosProducts, nextUrl } = await this._fetchProducts(this.nextUrl);
          if (!this.isConnected) return;

          this.nextUrl = nextUrl;
          this.hasMore = !!this.nextUrl;

          const targetGrid = phase === 'parent' ? this.parentGrid : this.grid;
          const toInsert = phase === 'parent' ? this._pickUnseen(products) : Array.from(products);

          if (phase === 'main') {
            this._registerHandles(products);
            if (this.oosGrid && oosProducts.length) {
              this._registerHandles(oosProducts);
              this._insertProductsSilent(oosProducts, this.oosGrid);
              this._syncOutOfStockVisibility();
            }
          }

          // Lot sans rien de visible : la sentinelle n'a pas bougé, elle se redéclenchera.
          // Au bout de MAX_EMPTY_STREAK lots d'affilée, on arrête de dérouler la source.
          // Un vélo réservé rangé dans la grille des disponibles ne compte pas comme lot utile.
          const availableCount = phase === 'main' ? toInsert.filter((node) => !node.hasAttribute('data-reserved-in-stock-zone') && !node.classList.contains('encart-media')).length : toInsert.length;
          this._emptyStreak = availableCount ? 0 : this._emptyStreak + 1;
          if (this._emptyStreak >= MAX_EMPTY_STREAK) {
            if (phase === 'main') {
              this._degradeSplit();
            } else {
              // Collection mère entièrement dédoublonnée : rien à proposer, on conclut.
              this._finish();
            }
          }

          if (toInsert.length && targetGrid) {
            await this.insertProducts(toInsert, targetGrid);
            if (phase === 'parent') this.parentSeparator?.classList.remove('is-hidden');
            this._postInsertHooks();
            if (phase === 'main') this._batchCount++;
            inserted = toInsert.length;
          } else {
            emptyFetches++;
          }

          if (!this.hasMore) await this._advancePhase();
        }

        this._retryCount = 0;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this._retryCount++;
          if (this._retryCount >= MAX_RETRY_COUNT) {
            console.error('Infinite scroll: max retries reached', error);
            this.hasMore = false;
            this._finish();
          } else {
            console.warn(`Infinite scroll: retry ${this._retryCount}/${MAX_RETRY_COUNT}`, error);
          }
        }
      } finally {
        this.isLoading = false;
        this.hideLoader();
        this._syncNextLink();
        if (!this.hasMore) {
          this.showEndMessage();
          this.observer?.disconnect();
        } else if (this.observer && this.sentinel) {
          // Re-observer le sentinel : il a été poussé plus bas par les nouveaux produits,
          // l'IntersectionObserver ne réévalue pas automatiquement sa position
          this.observer.unobserve(this.sentinel);
          this.observer.observe(this.sentinel);
        }
      }
    }

    /** Lien « page suivante » des robots : masqué, pointe sur la page à charger, retiré hors phase principale. */
    _syncNextLink() {
      if (!this.nextLink) return;
      if (!this.hasMore || this.phase !== 'main') {
        this.nextLink.remove();
        this.nextLink = null;
        return;
      }
      this.nextLink.href = this.nextUrl;
      this.nextLink.hidden = true;
    }

    /**
     * Passe à la phase suivante quand la source courante est épuisée.
     * main → parent (si une collection mère est déclarée, ou résolue depuis la recherche
     * prédictive) → done.
     */
    async _advancePhase() {
      if (this.phase === 'main' && this.parentGrid) {
        await this._resolveParentUrlFromSearch();
        if (this.parentUrl) {
          const parentUrl = this._buildParentUrl();
          if (parentUrl) {
            this.phase = 'parent';
            this.nextUrl = parentUrl;
            this.hasMore = true;
            this._emptyStreak = 0;
            // Sentinelle et loader passent sous la grille de la collection mère : laissés au-dessus,
            // ils resteraient dans le viewport et déclencheraient tous les lots d'un coup.
            // En mode dégradé elles sont déjà en fin de composant, sous tout le contenu visible.
            if (!this._splitDegraded) this._moveScrollAnchor(this.parentZone);
            this._syncEmptyStockState();
            return;
          }
        }
      }
      this._finish();
    }

    /**
     * Résout `parentUrl` depuis la recherche prédictive quand aucun metafield ne le fournit
     * (`/search` n'a pas de `data.parent_collection`, contrairement à une collection) : part des
     * catégories suggérées pour le terme tapé (décision utilisateur 2026-09-16, mesurée sur
     * `/search/suggest` — `cannondale` → 3 catégories, `mondraker` → 3, `enduro carbone` → 0).
     * Facultatif par construction : sans catégorie suggérée ni mère exploitable, `parentUrl` reste
     * null et `_advancePhase()` conclut normalement sur `_finish()`, sans zone ni séparateur
     * orphelin. Tentée une seule fois par instance (`_parentUrlAttempted`) : un échec réseau ne
     * doit pas relancer une requête à chaque lot du scroll infini.
     */
    async _resolveParentUrlFromSearch() {
      if (this.parentUrl || this._parentUrlAttempted) return;
      this._parentUrlAttempted = true;

      const term = this.parentSearchTerm;
      if (!term) return;

      try {
        const routes = (window.theme && window.theme.routes) || {};
        const suggestPath = routes.predictiveSearch || '/search/suggest';
        const query = [
          'q=' + encodeURIComponent(term),
          'section_id=predictive-search',
          'resources[type]=collection',
          'resources[limit]=' + PARENT_CANDIDATES_MAX,
          'resources[limit_scope]=each',
        ].join('&');

        const response = await fetch(`${suggestPath}?${query}`, { signal: this._abortController?.signal });
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Ancre exacte du markup rendu par snippets/predictive-search-panel.liquid.
        const candidats = [...doc.querySelectorAll('[data-suggestion-type="collection"]')]
          .map((a) => a.getAttribute('href'))
          .filter(Boolean);
        if (!candidats.length) return;

        /**
         * Premier tour — les catégories suggérées elles-mêmes, dans l'ordre rendu.
         * On ne prend PAS la première sans la sonder : Shopify suggère les collections par
         * correspondance de TITRE, sans regarder le stock, et la première est vide une fois sur
         * deux. Mesuré le 2026-09-16, produits en stock de chaque suggestion :
         *   cannondale -> vtc 0 · vtt 6 · gravel 6
         *   mondraker  -> gravel 0 · vtt 1 · mondraker 2
         *   vtt        -> black-friday 16 · kalkhoff 0 · pinarello 0
         * Le critère n'est pas « la collection a des produits », c'est « elle en a que la recherche
         * n'a PAS déjà montrés » : sur `mondraker`, la première candidate non vide (`vtt-mondraker`,
         * 1 produit en stock) ne contenait que le vélo déjà présent dans les résultats, le
         * dédoublonnage le retirait et le bloc restait vide malgré une sonde « réussie ».
         */
        const meres = [];
        for (const href of candidats) {
          const page = await this._chargerCandidate(href);
          if (!page) continue;
          const mere = page.querySelector('[data-parent-url]')?.getAttribute('data-parent-url');
          if (mere && meres.indexOf(mere) === -1) meres.push(mere);
          if (this._aDesInedits(page)) {
            this.parentUrl = href;
            return;
          }
        }

        /**
         * Second tour — leur collection MÈRE, plus large (décision utilisateur 2026-09-16).
         * Une catégorie suggérée dérive du même terme que la recherche : « VTT Cannondale » ne
         * peut contenir que des vélos que « cannondale » a déjà trouvés, donc elle n'apporte
         * structurellement rien. Mesuré ce jour-là sur `cannondale` (40 résultats affichés) :
         * `vtt-cannondale-reconditionne` 6 en stock / 0 inédit, `velo-gravel-cannondale-
         * reconditionne` 6 / 0 — le bloc ne se remplissait jamais.
         * On élargit donc par le metafield `data.parent_collection` de la candidate, c'est-à-dire
         * exactement la hiérarchie qu'une page collection suit déjà (cf. main-collection.liquid) :
         * `vtt-cannondale-reconditionne` → `velos-reconditionne` (50 produits en 1re page dont 11
         * Cannondale, donc 39 inédits), `velo-gravel-cannondale-reconditionne` →
         * `velo-gravel-reconditionne`. Il n'existe pas de niveau intermédiaire « VTT
         * reconditionné » (`/collections/vtt-reconditionne` répond 404) : la hiérarchie déclarée
         * saute directement à tous les vélos reconditionnés.
         * Coût total : 1 requête de suggestion + 3 candidates + 2 mères au pire, UNE SEULE FOIS
         * par page et seulement en fin de phase principale — jamais au chargement.
         */
        for (const mere of meres) {
          const page = await this._chargerCandidate(mere);
          if (page && this._aDesInedits(page)) {
            this.parentUrl = mere;
            return;
          }
        }

        // Ni catégorie suggérée ni mère n'apporte de vélo inédit : pas de bloc, pas de séparateur.
        this.parentUrl = null;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Infinite scroll: résolution de la collection mère (recherche) échouée', error);
        }
      }
    }

    /**
     * Charge une collection candidate avec les filtres et le tri de la recherche courante.
     * Renvoie son document, ou `null` si elle est injoignable — un candidat qui échoue ne doit
     * pas condamner les suivants.
     */
    async _chargerCandidate(href) {
      const url = this._buildParentUrl(href);
      if (!url) return null;
      try {
        const reponse = await fetch(url, { signal: this._abortController?.signal });
        if (!reponse.ok) return null;
        return new DOMParser().parseFromString(await reponse.text(), 'text/html');
      } catch (err) {
        if (err.name === 'AbortError') throw err;
        return null;
      }
    }

    /** Vrai si la collection chargée contient au moins un produit que la page n'a pas déjà montré. */
    _aDesInedits(page) {
      return [...page.querySelectorAll(GRID_SELECTOR + ' [data-product-handle]')].some(
        (noeud) => !this._seenHandles.has(noeud.dataset.productHandle),
      );
    }

    /** Fin de parcours : dévoile les produits hors stock de la collection courante */
    _finish() {
      this.phase = 'done';
      this.hasMore = false;
      this._oosRevealed = true;
      this._syncOutOfStockVisibility();
      this._syncEmptyStockState();
    }

    /**
     * Collection sans stock (ou presque) : plus aucun produit visible ne pousse la sentinelle,
     * qui se redéclencherait en boucle et avalerait toute la collection en arrière-plan.
     * On dévoile alors les hors stock tout de suite et on renvoie sentinelle et loader sous
     * l'ensemble du contenu — le scroll infini retrouve son fonctionnement normal, au prix
     * de produits en stock qui viendront s'insérer au-dessus du trait de séparation.
     */
    _degradeSplit() {
      if (this._splitDegraded) return;
      this._splitDegraded = true;
      this._oosRevealed = true;
      this._syncOutOfStockVisibility();
      this._syncEmptyStockState();
      this._moveScrollAnchorToEnd();
    }

    /** Le bloc hors stock ne s'affiche que dévoilé ET non vide (pas de trait orphelin) */
    _syncOutOfStockVisibility() {
      if (!this.oosZone) return;
      const hasItems = !!this.oosGrid && this.oosGrid.children.length > 0;
      const hidden = !this._oosRevealed || !hasItems;
      this.oosZone.classList.toggle('is-pending', hidden);
      // Redondant tant que la zone est masquée, mais c'est ce qui rend le trait visible :
      // il est rendu masqué côté serveur pour le cas sans JS.
      this.oosSeparator?.classList.toggle('is-hidden', hidden);
    }

    /**
     * État « aucun vélo disponible » : message dévoilé, et trait de séparation effacé — il ne
     * sépare plus rien puisque la grille au-dessus est vide. Le trait du bloc hors stock n'est
     * effacé que si la collection mère n'a rien apporté non plus.
     *
     * Évalué seulement une fois la collection courante entièrement parcourue (ou abandonnée) :
     * pendant la phase principale, un lot vide ne prouve rien, le suivant peut apporter du stock.
     */
    _syncEmptyStockState() {
      const stockExhausted = this.phase !== 'main' || this._splitDegraded;
      const noStock = stockExhausted && this.grid.children.length === 0;
      const noParentEither = noStock && (!this.parentGrid || this.parentGrid.children.length === 0);

      this.emptyMessage?.classList.toggle('is-hidden', !noStock);
      this.parentSeparator?.classList.toggle('is-flush', noStock);
      this.oosSeparator?.classList.toggle('is-flush', noParentEither);
    }

    /**
     * URL de la collection mère : mêmes filtres et même tri que la collection courante.
     * `filter.v.availability=1` est forcé — un filtre de variante matche un produit dès
     * qu'UNE variante correspond, même hors stock (cf. syncAvailabilityParam dans theme.js),
     * et ce bloc ne doit proposer que des vélos disponibles.
     */
    _buildParentUrl(base) {
      const source = base || this.parentUrl;
      if (!source) return null;
      let url;
      try {
        url = new URL(source, window.location.origin);
      } catch (e) {
        return null;
      }
      // `page` et `phcursor` ne sont volontairement pas repris : la pagination de la
      // collection mère repart de son premier lot.
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (!value) return;
        if (key.lastIndexOf('filter.', 0) === 0 || key === 'sort_by') {
          url.searchParams.set(key, value);
        }
      });
      url.searchParams.set('filter.v.availability', '1');
      return url.toString();
    }

    /** Déplace sentinelle et loader à la fin d'une zone (l'ordre relatif est conservé) */
    _moveScrollAnchor(zone) {
      if (!zone) return;
      if (this.sentinel) zone.appendChild(this.sentinel);
      if (this.loader) zone.appendChild(this.loader);
    }

    /** Renvoie sentinelle et loader sous tout le contenu du composant (avant le message de fin) */
    _moveScrollAnchorToEnd() {
      const ref = this.endMessage || null;
      if (this.sentinel) this.insertBefore(this.sentinel, ref);
      if (this.loader) this.insertBefore(this.loader, ref);
    }

    /** Mémorise les handles affichés — base du dédoublonnage de la collection mère */
    _registerHandles(nodes) {
      nodes.forEach((node) => {
        const handle = node.dataset && node.dataset.productHandle;
        if (handle) this._seenHandles.add(handle);
      });
    }

    /** Ne garde que les produits jamais affichés (les encarts média sont écartés) */
    _pickUnseen(nodes) {
      const picked = [];
      nodes.forEach((node) => {
        const handle = node.dataset && node.dataset.productHandle;
        if (!handle || this._seenHandles.has(handle)) return;
        this._seenHandles.add(handle);
        picked.push(node);
      });
      return picked;
    }

    /**
     * Fetch et parse les produits depuis une URL de pagination
     * @param {string} pageUrl - URL de la page suivante
     * @returns {Promise<{products: NodeList, oosProducts: NodeList, nextUrl: string|null}>}
     */
    async _fetchProducts(pageUrl) {
      const fetchUrl = this.buildFetchUrl(pageUrl);
      const response = await fetch(fetchUrl, { signal: this._abortController?.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return {
        // Extraire les nouveaux produits
        products: doc.querySelectorAll('#collection-grid > .grid__item'),
        // Produits hors stock du même lot, à empiler dans le bloc de fin de page
        oosProducts: doc.querySelectorAll('#collection-grid-oos > .grid__item'),
        // Extraire l'URL de la page suivante depuis le nouveau document
        nextUrl: doc.querySelector('infinite-scroll-collection[data-next-url]')?.dataset.nextUrl || null,
      };
    }

    /** Réinitialise AOS, QuickShop, SPR et FMD après insertion de produits */
    _postInsertHooks() {
      this.updateFmdSession();
      if (typeof theme !== 'undefined' && typeof theme.reinitProductGridItem === 'function') {
        // Debounce : si plusieurs batches arrivent en burst (scroll rapide), on attend 50ms
        // et on n'appelle reinitProductGridItem() (→ AOS.refreshHard()) qu'une seule fois.
        // Sans debounce : 5 batches en 500ms = 5× forced reflow = jank 200ms+ sur mobile.
        clearTimeout(this._postInsertDebounceId);
        this._postInsertDebounceId = setTimeout(() => {
          // Double rAF : attend un cycle paint complet (layout + composite)
          // avant de laisser AOS.refreshHard() recalculer les bounding rects.
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            requestAnimationFrame(() => {
              if (!this.isConnected) return;
              theme.reinitProductGridItem();
            });
          });
        }, 50);
      }
    }

    /** Observe les grid items pour offloader les images quand ils sont loin du viewport */
    _initOffloadObserver() {
      // Protéger la première rangée réelle (per_row items) — jamais offloadés (LCP)
      // ASSUMPTION: first-row items restent les N premiers enfants de la grille
      // (restore/infinite-scroll append toujours en fin de grille)
      const perRow = parseInt(this.dataset.perRow, 10) || 4;
      this._firstRowItems = new Set(
        [...this.grid.querySelectorAll(':scope > .grid__item:not(.encart-media)')].slice(0, perRow),
      );

      this._offloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._restoreImages(entry.target);
            } else if (!this._firstRowItems.has(entry.target)) {
              this._offloadImages(entry.target);
            }
          });
        },
        { rootMargin: OFFLOAD_ROOT_MARGIN, threshold: 0 },
      );
      [this.grid, this.parentGrid, this.oosGrid].forEach((grid) => {
        if (!grid) return;
        grid.querySelectorAll(':scope > .grid__item').forEach((item) => {
          this._offloadObserver.observe(item);
        });
      });
    }

    /** Retire src/srcset des images d'un item pour liberer la memoire GPU */
    _offloadImages(gridItem) {
      gridItem.querySelectorAll('img').forEach((img) => {
        if ((img.src || img.srcset) && !img.dataset.offloadedSrc && !img.dataset.offloadedSrcset) {
          if (img.src) img.dataset.offloadedSrc = img.src;
          if (img.srcset) img.dataset.offloadedSrcset = img.srcset;
          img.removeAttribute('src');
          img.removeAttribute('srcset');
        }
      });
    }

    /** Restaure les images d'un item qui revient pres du viewport */
    _restoreImages(gridItem) {
      gridItem.querySelectorAll('img[data-offloaded-src], img[data-offloaded-srcset]').forEach((img) => {
        if (img.dataset.offloadedSrc) {
          img.src = img.dataset.offloadedSrc;
          delete img.dataset.offloadedSrc;
        }
        if (img.dataset.offloadedSrcset) {
          img.srcset = img.dataset.offloadedSrcset;
          delete img.dataset.offloadedSrcset;
        }
        img.decode?.().catch(() => {});
      });
    }

    async insertProducts(products, targetGrid) {
      const grid = targetGrid || this.grid;
      const fragment = document.createDocumentFragment();
      const newItems = [];
      products.forEach((product) => {
        const clone = product.cloneNode(true);
        clone.classList.add('infinite-scroll__item--entering');
        fragment.appendChild(clone);
        newItems.push(clone);
      });

      grid.appendChild(fragment);

      // Observer les nouveaux items pour l'offload d'images
      newItems.forEach((item) => this._offloadObserver?.observe(item));

      // Trigger reflow pour que l'animation fonctionne
      // eslint-disable-next-line no-unused-expressions
      grid.offsetHeight;

      // Double rAF partagé : garantit que l'état --entering est commis au compositing
      // avant toute transition. Corrige le pop desktop (content-visibility: auto diffère
      // l'activation-rendering des nouveaux éléments → premiers items avec stagger 0ms/50ms
      // n'ont pas d'état "from" peint → transition CSS ne joue pas → pop).
      // Note : animations et cleanup sont fire-and-forget (non awaitable depuis loadMore).
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          newItems.forEach((item, index) => {
            const id = setTimeout(() => {
              item.classList.remove('infinite-scroll__item--entering');
              item.classList.add('infinite-scroll__item--entered');
              // Guard data-aos : synchronise .aos-animate avec --entered (évite pop image/container).
              // _insertProductsSilent (restore) supprime data-aos → guard empêche action sur ce chemin.
              if (item.hasAttribute('data-aos') && !item.classList.contains('aos-animate')) {
                item.classList.add('aos-animate');
              }
            }, index * STAGGER_DELAY_MS);
            this._animationTimers.push(id);
          });

          // Cleanup ancré au même instant que les setTimeouts d'animation (même starting point).
          // Tous les timers du batch ont forcément fini ici → vider le tableau pour éviter
          // l'accumulation d'IDs expirés sur les longues sessions (50+ pages scrollées).
          const cleanupId = setTimeout(
            () => {
              newItems.forEach((item) => item.classList.remove('infinite-scroll__item--entered'));
              this._animationTimers = [];
            },
            products.length * STAGGER_DELAY_MS + ANIMATION_CLEANUP_MS,
          );
          this._animationTimers.push(cleanupId);
        });
      });
    }

    /**
     * Construit l'URL de fetch en ajoutant les paramètres FMD et sort_by si nécessaires
     * @param {string} url - URL de pagination originale
     * @returns {string} - URL avec paramètres FMD et sort_by
     */
    buildFetchUrl(url) {
      if (!url) return url;

      const urlObj = new URL(url, window.location.origin);
      const currentUrlParams = new URLSearchParams(window.location.search);

      // Préserver le tri depuis l'URL actuelle s'il n'est pas dans l'URL de pagination
      if (!urlObj.searchParams.has('sort_by')) {
        const sortBy = currentUrlParams.get('sort_by');
        if (sortBy) urlObj.searchParams.set('sort_by', sortBy);
      }

      // Parcours partenaire FMD : localStorage d'abord, puis URL actuelle en fallback
      if (!urlObj.searchParams.has('fmd')) {
        let fmdHandle;
        try {
          fmdHandle = localStorage.getItem('fmd');
        } catch (e) {
          /* Safari privé */
        }
        fmdHandle = fmdHandle || currentUrlParams.get('fmd');
        if (fmdHandle) urlObj.searchParams.set('fmd', fmdHandle);
      }

      // Parcours générique FMD : localStorage d'abord, puis URL actuelle en fallback
      if (!urlObj.searchParams.has('fmd-generic')) {
        let fmdGenericStored = false;
        try {
          fmdGenericStored = localStorage.getItem('fmd-generic-flow') === 'true';
        } catch (e) {
          /* Safari privé */
        }
        if (fmdGenericStored || currentUrlParams.has('fmd-generic')) {
          urlObj.searchParams.set('fmd-generic', '1');
        }
      }

      return urlObj.toString();
    }

    updateFmdSession() {
      // Réinitialiser FmdSession pour intercepter les nouveaux liens produits
      if (window.FmdSession) {
        try {
          new window.FmdSession();
        } catch (e) {
          console.warn('FmdSession update failed:', e);
        }
      }
    }

    // --- Restauration du scroll ---

    /** Détecte une navigation back/forward (PerformanceNavigationTiming + fallback legacy) */
    _isBackForward() {
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'back_forward') return true;
        // Fallback Safari < 16.4 : PerformanceNavigation.type === 2
        if (performance.navigation && performance.navigation.type === 2) return true;
        return false;
      } catch (e) {
        return false;
      }
    }

    _shouldRestore() {
      if (!this._isBackForward()) return false;
      try {
        const saved = JSON.parse(sessionStorage.getItem('infinite-scroll-state'));
        if (!saved) return false;
        return saved.pathname === window.location.pathname && saved.search === window.location.search;
      } catch (e) {
        return false;
      }
    }

    async _restoreState() {
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem('infinite-scroll-state'));
      } catch (e) {
        saved = null;
      }

      if (!saved) {
        this._initNormal();
        return;
      }

      this._batchCount = 0;
      this.showLoader();

      const timeoutId = setTimeout(() => this._abortController?.abort(), RESTORE_TIMEOUT_MS);

      try {
        // Note : les encarts média ont des positions aléatoires côté serveur,
        // le grid restauré peut différer légèrement de l'original.
        // Seuls les lots de la collection courante sont restaurés : la phase collection mère
        // se rejoue normalement au scroll.
        if (saved.batchCount > 0) {
          await this._fetchRestoredBatches(saved);
          if (this.isConnected) this._postInsertHooks();
        }

        if (this.isConnected) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => window.scrollTo(0, saved.scrollY));
          });
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('Infinite scroll restore failed:', e);
        }
      } finally {
        clearTimeout(timeoutId);
        // Toujours recreer l'AbortController (l'ancien est potentiellement aborte par le timeout)
        this._abortController = new AbortController();
        if (this.isConnected) {
          this.hideLoader();
          this._initNormal();
        }
      }
    }

    /**
     * Fetch les batches sauvegardés par chunks de 3 (limite la concurrence).
     * Stoppe au premier échec pour éviter des trous dans la grille.
     */
    async _fetchRestoredBatches(saved) {
      const urls = [];
      for (let i = 2; i <= saved.batchCount + 1; i++) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', i);
        urls.push(this.buildFetchUrl(url.toString()));
      }

      for (let i = 0; i < urls.length; i += RESTORE_CHUNK_SIZE) {
        if (!this.isConnected) break;
        const chunk = urls.slice(i, i + RESTORE_CHUNK_SIZE);
        const results = await Promise.allSettled(chunk.map((u) => fetch(u, { signal: this._abortController?.signal })));

        let failed = false;
        for (let k = 0; k < results.length; k++) {
          if (results[k].status !== 'fulfilled' || !results[k].value.ok) {
            console.warn('Infinite scroll restore: page', i + k + 2, 'failed');
            failed = true;
            break;
          }
          const html = await results[k].value.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const products = doc.querySelectorAll('#collection-grid > .grid__item');
          if (products.length > 0) {
            this._registerHandles(products);
            this._insertProductsSilent(products);
            this._batchCount++;
          }
          const oosProducts = doc.querySelectorAll('#collection-grid-oos > .grid__item');
          if (this.oosGrid && oosProducts.length > 0) {
            this._registerHandles(oosProducts);
            this._insertProductsSilent(oosProducts, this.oosGrid);
            this._syncOutOfStockVisibility();
          }
          // Mettre à jour nextUrl depuis chaque page traitée (état cohérent même si restore partiel)
          const nextEl = doc.querySelector('infinite-scroll-collection[data-next-url]');
          this.nextUrl = nextEl ? nextEl.dataset.nextUrl : null;
          this.hasMore = !!this.nextUrl;
        }
        if (failed) break;
      }
    }

    _insertProductsSilent(products, targetGrid) {
      const grid = targetGrid || this.grid;
      const fragment = document.createDocumentFragment();
      const clones = [];
      products.forEach((product) => {
        const clone = product.cloneNode(true);
        clone.removeAttribute('data-aos');
        clone.querySelectorAll('[data-aos]').forEach((el) => el.removeAttribute('data-aos'));
        fragment.appendChild(clone);
        clones.push(clone);
      });
      grid.appendChild(fragment);
      // Observer les clones pour l'offload d'images
      clones.forEach((item) => this._offloadObserver?.observe(item));
    }

    /** Initialise l'état normal (observer ou message de fin + event handlers) */
    async _initNormal() {
      // Collection déjà entièrement rendue côté serveur : enchaîner tout de suite sur la
      // collection mère, ou dévoiler les hors stock s'il n'y en a pas. Attendu : _advancePhase()
      // peut résoudre la collection mère via un fetch réseau (recherche prédictive) avant de
      // savoir si `hasMore` doit repasser à true.
      if (!this.hasMore && this.phase !== 'done') await this._advancePhase();

      if (this.hasMore) {
        this.initObserver();
      } else {
        this.showEndMessage();
      }
      this._syncNextLink();
      this._initOffloadObserver();
      this._bindPagehide();
      this._bindPageshow();
    }

    _bindPagehide() {
      if (this._pagehideHandler) {
        window.removeEventListener('pagehide', this._pagehideHandler);
      }
      if (!this.isConnected) return;

      this._pagehideHandler = () => {
        try {
          sessionStorage.setItem(
            'infinite-scroll-state',
            JSON.stringify({
              pathname: window.location.pathname,
              search: window.location.search,
              scrollY: window.scrollY,
              batchCount: this._batchCount || 0,
            }),
          );
        } catch (e) {
          // sessionStorage indisponible (Safari mode privé)
        }
      };
      window.addEventListener('pagehide', this._pagehideHandler);
    }

    /** Restaure le scroll sur retour bfcache (DOM déjà complet, pas de re-fetch) */
    _bindPageshow() {
      if (this._pageshowHandler) {
        window.removeEventListener('pageshow', this._pageshowHandler);
      }
      if (!this.isConnected) return;

      this._pageshowHandler = (event) => {
        if (!event.persisted) return;
        try {
          const saved = JSON.parse(sessionStorage.getItem('infinite-scroll-state'));
          if (!saved) return;
          if (saved.pathname !== window.location.pathname) return;
          if (saved.search !== window.location.search) return;
          window.scrollTo(0, saved.scrollY);
        } catch (e) {
          // Ignore
        }
      };
      window.addEventListener('pageshow', this._pageshowHandler);
    }

    showLoader() {
      if (this.loader) this.loader.style.display = 'flex';
    }

    hideLoader() {
      if (this.loader) this.loader.style.display = 'none';
    }

    showEndMessage() {
      if (this.endMessage) this.endMessage.classList.add('is-visible');
      if (this.loader) this.loader.style.display = 'none';
    }
  }

  customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
}
